<script lang="ts" setup>
import { tpgIconArrowDown } from '@tpg/grammar-web-icons';
import {
  computed,
  onBeforeUnmount,
  onMounted,
  onUpdated,
  ref,
  useTemplateRef,
} from 'vue';
import { useI18n } from 'vue-i18n';

import type {
  GrammarComboBoxOptions,
  TpgChangeEvent,
} from '@nora-monorepo/grammar-ui';
import {
  GrammarComboBox,
  GrammarIcon,
  GrammarText,
} from '@nora-monorepo/grammar-ui';
import type {
  GroupResultDTO,
  UserResultDTO,
} from '@nora-monorepo/nora-api-client';
import { formatName } from '@nora-monorepo/shared';

import AvatarImage from '#ui-shared/components/avatar-image/AvatarImage.vue';
import type { UsersAndGroups } from '#ui-shared/components/member-picker/member-picker-types';
import MemberPickerUserItem from '#ui-shared/components/member-picker/MemberPickerUserItem.vue';
import MemberSearchPicker from '#ui-shared/components/member-picker/MemberSearchPicker.vue';
import {
  deselectVisibleGroups,
  deselectVisibleUsers,
  selectVisibleGroups,
  selectVisibleUsers,
} from '#ui-shared/components/member-picker/operation-member-picker-utils';
import { useRoleStore } from '#ui-shared/stores/role-store';

interface OperationMemberPickerProps {
  enabled?: boolean;
  error?: string;
}

const props = withDefaults(defineProps<OperationMemberPickerProps>(), {
  enabled: true,
});

const roleStore = useRoleStore();
const selectedOperationUsersAndGroups = defineModel<UsersAndGroups>({ required: true });

const { t } = useI18n();
const filterString = ref<string>('');
const shouldShowSelectedMembersScrollHint = ref(false);
const selectedMembersListRef = useTemplateRef<HTMLElement>('selectedMembersListRef');
const selectedMembersScrollContainerRef = useTemplateRef<HTMLElement>('selectedMembersScrollContainerRef');
let selectedMembersResizeObserver: ResizeObserver | null = null;

const defaultRole = computed(() => {
  const defaultRole = roleStore.defaultOperationRole;
  if (!defaultRole) {
    throw new Error('Could not find default role');
  }
  return defaultRole;
});

const availableLocalRoles = computed(() => {
  const available = roleStore.availableOperationRoles;
  if (!available) {
    throw new Error('Could not find available operation roles');
  }
  return available;
});

/**
 * Adds a given user to the selectedMembers list.
 * @param newMember
 * @param roleId
 */
function addUser(newMember: UserResultDTO, roleId?: string): void {
  const standardizedRoleId = roleId ?? defaultRole.value?.id;
  if (!availableLocalRoles.value.some(role => role.id === standardizedRoleId)) {
    throw new Error('Could not find local role');
  }

  // We need to do direct assignment because otherwise vee-validate won't pick up on the change.
  selectedOperationUsersAndGroups.value = {
    ...selectedOperationUsersAndGroups.value,
    users: [
      ...selectedOperationUsersAndGroups.value.users,
      { user: newMember, role: standardizedRoleId },
    ],
  };
}

/**
 * Adds the specified group to the list of selected members of an operation. If no role is provided, the default will be given.
 * @param newGroup
 * @param roleId
 */
function addGroup(newGroup: GroupResultDTO, roleId?: string): void {
  const standardizedRoleId = roleId ?? defaultRole.value?.id;
  if (!availableLocalRoles.value.some(role => role.id === standardizedRoleId)) {
    throw new Error('Could not find local role');
  }

  // We need to do direct assignment because otherwise vee-validate won't pick up on the change.
  selectedOperationUsersAndGroups.value = {
    ...selectedOperationUsersAndGroups.value,
    groups: [
      ...selectedOperationUsersAndGroups.value.groups,
      { group: newGroup, role: standardizedRoleId },
    ],
  };
}

function removeUserById(memberToRemoveId: string): void {
  const memberToRemove = selectedOperationUsersAndGroups.value.users.find(member => member.user.id === memberToRemoveId);
  if (memberToRemove?.nonRemovable) {
    return;
  }

  selectedOperationUsersAndGroups.value = {
    ...selectedOperationUsersAndGroups.value,
    users: selectedOperationUsersAndGroups.value.users.filter(
      member => member.user.id !== memberToRemoveId,
    ),
  };
}

function removeGroupById(memberToRemoveId: string): void {
  selectedOperationUsersAndGroups.value = {
    ...selectedOperationUsersAndGroups.value,
    groups: selectedOperationUsersAndGroups.value.groups.filter(member => member.group.id !== memberToRemoveId),
  };
}

function toggleUser(memberToRemove: UserResultDTO): void {
  if (
    selectedOperationUsersAndGroups.value.users.some(member => member.user.id === memberToRemove.id)
  ) {
    removeUserById(memberToRemove.id);
  }
  else {
    if (!defaultRole.value) {
      throw new Error('Could not find default role');
    }

    addUser(memberToRemove, defaultRole.value.id);
  }
}

function toggleGroup(memberToRemove: GroupResultDTO): void {
  if (
    selectedOperationUsersAndGroups.value.groups.some(
      member => member.group.id === memberToRemove.id,
    )
  ) {
    removeGroupById(memberToRemove.id);
  }
  else {
    if (!defaultRole.value) {
      throw new Error('Could not find default role');
    }

    addGroup(memberToRemove, defaultRole.value.id);
  }
}

function updateUserRoleById(userId: string, roleId: string): void {
  if (!availableLocalRoles.value.some(role => role.id === roleId)) {
    throw new Error('Could not find local role');
  }

  selectedOperationUsersAndGroups.value = {
    ...selectedOperationUsersAndGroups.value,
    users: selectedOperationUsersAndGroups.value.users.map(member => member.user.id === userId ? { ...member, role: roleId } : member),
  };
}

function onSelectAllUsers(shouldSelectAllUsers: boolean, users: UserResultDTO[]): void {
  if (shouldSelectAllUsers) {
    const roleId = defaultRole.value.id;
    if (!availableLocalRoles.value.some(role => role.id === roleId)) {
      throw new Error('Could not find local role');
    }

    selectedOperationUsersAndGroups.value = selectVisibleUsers(
      selectedOperationUsersAndGroups.value,
      users,
      roleId,
    );
    return;
  }

  selectedOperationUsersAndGroups.value = deselectVisibleUsers(
    selectedOperationUsersAndGroups.value,
    users,
  );
}

function onSelectAllGroups(shouldSelectAllGroups: boolean, groups: GroupResultDTO[]): void {
  if (shouldSelectAllGroups) {
    const roleId = defaultRole.value.id;
    if (!availableLocalRoles.value.some(role => role.id === roleId)) {
      throw new Error('Could not find local role');
    }

    selectedOperationUsersAndGroups.value = selectVisibleGroups(
      selectedOperationUsersAndGroups.value,
      groups,
      roleId,
    );
    return;
  }

  selectedOperationUsersAndGroups.value = deselectVisibleGroups(
    selectedOperationUsersAndGroups.value,
    groups,
  );
}

function roleNameFromId(id: string) {
  return availableLocalRoles.value.find(role => role.id === id)?.name ?? 'Role';
}

function onGroupRoleChange(groupId: string, event: TpgChangeEvent<string>) {
  const roleId = event.value;
  if (!roleId) {
    return;
  }
  selectedOperationUsersAndGroups.value = {
    ...selectedOperationUsersAndGroups.value,
    groups: selectedOperationUsersAndGroups.value.groups.map(member =>
      member.group.id === groupId ? { ...member, role: roleId } : member,
    ),
  };
}

const roleOptions = computed((): GrammarComboBoxOptions[] => {
  return availableLocalRoles.value.map(role => ({
    label: role.name,
    value: role.id,
  }));
});

function updateSelectedMembersScrollHint(): void {
  const selectedMembersScrollContainer = selectedMembersScrollContainerRef.value;
  if (!selectedMembersScrollContainer) {
    shouldShowSelectedMembersScrollHint.value = false;

    return;
  }

  const hasOverflow = selectedMembersScrollContainer.scrollHeight > selectedMembersScrollContainer.clientHeight + 1;
  const hasMoreContentBelow = selectedMembersScrollContainer.scrollTop + selectedMembersScrollContainer.clientHeight < selectedMembersScrollContainer.scrollHeight - 1;

  shouldShowSelectedMembersScrollHint.value = hasOverflow && hasMoreContentBelow;
}

onMounted(() => {
  updateSelectedMembersScrollHint();

  if ('ResizeObserver' in window) {
    selectedMembersResizeObserver = new ResizeObserver(() => {
      updateSelectedMembersScrollHint();
    });

    if (selectedMembersScrollContainerRef.value) {
      selectedMembersResizeObserver.observe(selectedMembersScrollContainerRef.value);
    }

    if (selectedMembersListRef.value) {
      selectedMembersResizeObserver.observe(selectedMembersListRef.value);
    }
  }
});

onUpdated(() => {
  updateSelectedMembersScrollHint();
});

onBeforeUnmount(() => {
  selectedMembersResizeObserver?.disconnect();
});
</script>

<template>
  <div class="p-2 rounded-xs h-full flex flex-row overflow-hidden">
    <div
      class="flex-1 h-full flex flex-col border-r-light-separator-transparency dark:border-r-dark-separator-transparency border-r p-3"
    >
      <MemberSearchPicker
        v-model:filter-string="filterString"
        :current-member-list="selectedOperationUsersAndGroups"
        :enabled="props.enabled"
        @toggle-user="toggleUser"
        @toggle-group="toggleGroup"
        @select-all-groups="onSelectAllGroups"
        @select-all-users="onSelectAllUsers"
      />
    </div>

    <div class="p-3 flex-1 h-full min-h-0 flex flex-col">
      <span class="dark:text-dark-label-secondary text-light-label-secondary mr-auto text-sm font-semibold mb-2">
        {{ t('conversationDialog.selectedMembers') }}
      </span>
      <span
        v-if="props.error"
        data-test="selected-members-warning"
        class="mb-2 block text-xs text-[#ff1fd2]"
      >
        {{ props.error }}
      </span>
      <div class="relative flex-1 min-h-0">
        <div
          ref="selectedMembersScrollContainerRef"
          class="h-full overflow-y-auto pr-2"
          @scroll="updateSelectedMembersScrollHint"
        >
          <!-- The following should probably be made into a standalone component type, since it represent a list of things that must satisfy some validation logic -->
          <div
            ref="selectedMembersListRef"
            data-test="selected-members-list"
            class="bg-light-bg-primary dark:bg-dark-bg-primary px-3 rounded-sm border" :class="props.error
              ? 'border-[var(--nora-error)]'
              : 'border-transparent'"
          >
            <div
              v-for="user in selectedOperationUsersAndGroups.users"
              :key="`${user.user.id}+${user.role}`"
              class="flex flex-row items-center w-full px-2 py-4 border-b-light-separator-transparency dark:border-b-dark-separator-transparency border-b last:border-b-0"
            >
              <MemberPickerUserItem :user="user" :role-options="roleOptions" @remove-user="removeUserById" @update-user-role="updateUserRoleById" />
            </div>

            <div
              v-for="member in selectedOperationUsersAndGroups.groups"
              :key="member.group.id"
              class="flex flex-row items-center w-full px-2 py-4 border-b-light-separator-transparency dark:border-b-dark-separator-transparency border-b last:border-b-0"
            >
              <AvatarImage :id="member.group.id" class="mr-2" icon="groups" />

              <span class="mx-1 flex-1">
                <GrammarText :text="formatName(member.group) ?? ''" />
              </span>

              <div class="w-28 mr-3">
                <GrammarComboBox
                  :label="roleNameFromId(member.role) ?? 'Role'"
                  :selected-value="member.role"
                  :options="roleOptions"
                  :full-width="true"
                  @tpg-change="(event) => onGroupRoleChange(member.group.id, event)"
                />
              </div>
              <div class="self-center w-10">
                <!-- Spacer to align with user items that have OptionsMenu -->
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="shouldShowSelectedMembersScrollHint"
          class="pointer-events-none absolute -bottom-2 right-1 z-10 flex h-12 w-12 items-center justify-center text-light-label-secondary leading-none dark:text-dark-label-secondary"
        >
          <GrammarIcon :icon-template="tpgIconArrowDown" size="xlarge" class="block" />
        </div>
      </div>
    </div>
  </div>
</template>
